/**
 * The EDITABLE copy of an AI proposal, and the arithmetic around it.
 *
 * The proposal itself is immutable — it is what the model said, and the harness
 * compares it against what was actually saved. So the chips the user tugs at
 * live in a separate draft, seeded from the proposal, and `editsCount` is the
 * distance between the two. Mutating the proposal in place would erase the only
 * signal that tells us whether the model is any good.
 *
 * ⚠️ `suggestedExcludes` start UNSELECTED. An exclusion the user never agreed
 * to is a filter that silently drops lots, and the one thing worse than a noisy
 * alert is a quiet one for a reason nobody can see. They render as «+» chips.
 */
import type {
  AiCategoryRef,
  AiIkpuRef,
  AiNicheRef,
  AiProposal,
  AiRegionRef,
} from './ai-search-client';

export interface AiDraft {
  name: string;
  categories: AiCategoryRef[];
  ikpu: AiIkpuRef[];
  niches: AiNicheRef[];
  keywords: string[];
  regions: AiRegionRef[];
  /** Exclusions the user has ACCEPTED (a subset of `suggestedExcludes`, plus
   *  anything typed). Empty on arrival — see the note above. */
  excludes: string[];
  minBudgetUzs: number | null;
}

/** Seed a draft from a fresh proposal. */
export function draftFromProposal(p: AiProposal): AiDraft {
  return {
    name: p.name,
    categories: [...p.categories],
    ikpu: [...p.ikpu],
    niches: [...p.niches],
    keywords: [...p.keywords],
    regions: [...p.regions],
    excludes: [],
    minBudgetUzs: p.minBudgetUzs,
  };
}

/** Everything a search needs to match on — the "subject" axis the API insists
 *  on having at least one of. */
export function subjectCount(d: AiDraft): number {
  return d.categories.length + d.ikpu.length + d.niches.length;
}

/** True when the draft could be saved at all (the API's own rule). */
export function isSaveable(d: AiDraft): boolean {
  return subjectCount(d) > 0;
}

function diffSize(before: string[], after: string[]): number {
  const b = new Set(before);
  const a = new Set(after);
  let n = 0;
  for (const x of b) if (!a.has(x)) n++;
  for (const x of a) if (!b.has(x)) n++;
  return n;
}

/**
 * Distance between two drafts, as a count of individual changes.
 *
 * Deliberately a COUNT of chips and fields, not a boolean: «принял без правок»
 * and «снял один код из пяти» are the same boolean and very different facts
 * about the model. The scale is per-item, so removing three codes reads as 3;
 * one field changed (name, budget floor) is 1 each.
 */
export function countDraftEdits(before: AiDraft, after: AiDraft): number {
  let n = 0;
  n += diffSize(
    before.categories.map((c) => c.slug),
    after.categories.map((c) => c.slug),
  );
  n += diffSize(
    before.ikpu.map((c) => c.code),
    after.ikpu.map((c) => c.code),
  );
  n += diffSize(
    before.niches.map((c) => c.slug),
    after.niches.map((c) => c.slug),
  );
  n += diffSize(before.keywords, after.keywords);
  n += diffSize(
    before.regions.map((r) => r.code),
    after.regions.map((r) => r.code),
  );
  n += diffSize(before.excludes, after.excludes);
  if ((before.minBudgetUzs ?? null) !== (after.minBudgetUzs ?? null)) n++;
  if (before.name.trim() !== after.name.trim()) n++;
  return n;
}

/**
 * How far the draft has moved from the PROPOSAL.
 *
 * The baseline for exclusions is `[]` — accepting a suggested exclusion is the
 * user correcting the proposal, even though the chip was offered, and
 * `draftFromProposal` is what defines that.
 *
 * ⚠️ Not the right function for the saved-search BUILDER, which fills an
 * existing search: there the baseline is the form as it stood at the moment of
 * the fill, not the proposal, or every value the search already carried would
 * be counted as an edit the user never made. Use `countDraftEdits` with a
 * snapshot there.
 */
export function countEdits(p: AiProposal, d: AiDraft): number {
  return countDraftEdits(draftFromProposal(p), d);
}

/**
 * The create payload for a draft.
 *
 * Goes to the SAME `POST /me/saved-searches` the manual flow uses — an AI
 * search is an ordinary saved search, and anything else would give it different
 * matching, a different digest and a second set of bugs. `aiDraftId` is the
 * only addition, and the server uses it to close the draft out as
 * saved/edited_saved by itself.
 *
 * ⚠️ WHAT THIS ENDPOINT CAN AND CANNOT EXPRESS (read before "simplifying"):
 *  - categories → comma-joined into the legacy `categorySlug` scalar, which the
 *    whole stack already reads as a UNION (catalog `category` param,
 *    `buildWhere`, `criteriaFromLegacy`);
 *  - ИКПУ, ниши, регионы, слова и исключения → the plural `ikpuCodes` /
 *    `nicheSlugs` / `regionCodes` / `textTerms` / `excludeTerms`, each of which
 *    the repository patches into `criteria` IN FULL.
 *
 * Regions and words used to have no plural form here, so the tail of a
 * multi-region / multi-word proposal followed in a second PATCH. That is gone:
 * one call now carries the whole proposal, so the search is never briefly (or,
 * if the PATCH failed, permanently) narrower than what the user approved — and
 * the server can compare what was saved against what was proposed, which is the
 * only way «приняли без правок» is measurable at all.
 *
 * Joining either axis into its scalar would NOT work and never did: a
 * comma-joined region is one unknown code, and joined words compile to one
 * phrase — both match nothing, silently.
 */
export function draftToCreateInput(
  d: AiDraft,
  opts: { aiDraftId?: string; replace?: boolean } = {},
): {
  name: string;
  categorySlug?: string;
  ikpuCodes?: string[];
  nicheSlugs?: string[];
  regionCodes?: string[];
  textTerms?: string[];
  excludeTerms?: string[];
  minBudgetUzs?: number;
  aiDraftId?: string;
  replace?: boolean;
} {
  return {
    name: d.name.trim(),
    ...(d.categories.length ? { categorySlug: d.categories.map((c) => c.slug).join(',') } : {}),
    ...(d.ikpu.length ? { ikpuCodes: d.ikpu.map((c) => c.code) } : {}),
    ...(d.niches.length ? { nicheSlugs: d.niches.map((n) => n.slug) } : {}),
    ...(d.regions.length ? { regionCodes: d.regions.map((r) => r.code) } : {}),
    ...(d.keywords.length ? { textTerms: d.keywords } : {}),
    ...(d.excludes.length ? { excludeTerms: d.excludes } : {}),
    ...(d.minBudgetUzs != null ? { minBudgetUzs: d.minBudgetUzs } : {}),
    ...(opts.aiDraftId ? { aiDraftId: opts.aiDraftId } : {}),
    ...(opts.replace ? { replace: true } : {}),
  };
}
